import React, { useState } from 'react';
import { Button, Nav } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { FadeModal, NavButton } from './Transitions';
import { DashboardIcon, FaqsIcon, FileTransferIcon, MessagingIcon, PlusIcon2, RetestIcon, TestsIcon, UserIcon } from './Icons';
import Logo from './Logo';
import EditProject from '../Popups/EditProject';
import { useUser } from './RoleContext';
import EditChat from '../Popups/EditChat';





interface SidebarProps {
	data_bs_theme: string;

}

const Sidebar: React.FC<SidebarProps> = ({ data_bs_theme }) => {
	const location = useLocation();
	const navigate = useNavigate();
	const [show, setShow] = useState(false);
	const handleHide = () => setShow(false);
	const [complete, setComplete] = useState(false);
	const handleComplete = () => { setComplete(true); window.location.reload(); };
	const handleShow = () => { setComplete(false); setShow(true) };
	const { highestPrivilegeRole } = useUser();
	
	 const canManageUsers =
    highestPrivilegeRole === 'Super User' || highestPrivilegeRole === 'Project Manager';
	const modals: Record<string, { text: string; modal: React.ReactNode }> = {
		default: { text: 'New Project', modal: <EditProject action='create' close={handleHide} complete={handleComplete} /> },
		 '/messaging':{text:'New Conversation',modal: <EditChat  /> }

	};
	const isUserManagement = location.pathname === '/usermanagement';
	const props = modals[location.pathname as keyof typeof modals] || modals.default;

	// A pure path check, exactly like isUserManagement above: no fetch, no status gate. The tab
	// must not be gated on the project being delivered — accepting a retest quote moves the
	// parent Jira ticket to "Project Delivery", which is not a delivered status, so a
	// delivered-only gate would make the tab vanish while a retest is actually in flight.
	// `/projects` on its own is the list, not a project, so the id segment must be non-empty. The
	// second segment is the Canopy id only when it is numeric — 'quote', 'setup' and 'retest' are
	// route names, not projects. Carrying it through matters: the retest page and Crumbs both key
	// off it, and a URL without it leaves the breadcrumb trail unable to resolve the project.
	const [, projectId, canopyId] = location.pathname.match(/^\/projects\/([^/]+)(?:\/(\d+))?/) ?? [];
	const retestPath = projectId
		? `/projects/${projectId}${canopyId ? `/${canopyId}` : ''}/retests`
		: undefined;
	// The retest flow is two pages: the table at /retests and the request wizard at /retest, which
	// is reached from it and returns to it. Both belong to the Retest item. Without this, NavLink
	// counts any descendant path as a match, so '/projects' stays lit across the whole flow and
	// two items read as selected — and the wizard, which Retest does not point at, hands the
	// selection back to Project halfway through.
	const isRetestFlow = !!projectId && /\/retests?$/.test(location.pathname);

	return (
		<aside className='position-fixed top-0 start-0 shadow d-flex flex-column' data-bs-theme={data_bs_theme}>
			<Logo />
			<Nav className='flex-column'>
				<h6>Overview</h6>
				<NavButton to='/dashboard'><DashboardIcon />Dashboard</NavButton>
				<NavButton to='/projects' inactive={isRetestFlow}><TestsIcon />Project</NavButton>
				{/* style={{}} clears the icon's own default marginTop so the shared
				    `aside .nav-link svg` rule positions it like every other nav icon. */}
				{retestPath && (<NavButton to={retestPath} active={isRetestFlow}><RetestIcon style={{}} />Retest</NavButton>)}
				<NavButton to='/file-transfer'><FileTransferIcon />File Transfer</NavButton>
				{canManageUsers && (<NavButton to='/usermanagement'><UserIcon /> User Management</NavButton>)}
				<h6>Support</h6>
				<NavButton to='/faqs'><FaqsIcon />FAQs</NavButton>
				<NavButton to='/messaging'><MessagingIcon />Messaging</NavButton>
			</Nav>
			{canManageUsers && (
				<>
					{/* For everything except User Management, keep the existing modal behaviour */}
					{!isUserManagement && (
						<FadeModal
							show={show}
							body={props.modal}
							title={complete ? '' : props.text}
							onHide={handleHide}
						>
							<Button
								variant="secondary"
								className="w-100 mt-auto"
								onClick={handleShow}
							>
								<PlusIcon2 />
								{props.text}
							</Button>
						</FadeModal>
					)}

					{/* On /usermanagement, just trigger ?newUser=1 */}
					{isUserManagement && (
						<Button
							variant="secondary"
							className="w-100 mt-auto"
							onClick={() => navigate('/usermanagement?newUser=1')}
						>
							<PlusIcon2 />
							New User
						</Button>
					)}
				</>
			)}

		</aside>
	);
};

export default Sidebar;